import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Avatar,
    MenuItem,
    Select,
    InputAdornment,
    Input
} from '@mui/material';
import { Plus, Trash2, SquarePen, Upload, X, Search } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getAllSearchGurus, createSearchGuru, updateSearchGuru, deleteSearchGuru } from '../../Services/ContentHub/SearchGurusApi';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';
import { generateSlug, normalizeSlugInput, cleanSlugFinal } from '../../utils.js/slugUtils';
import GlobalPagination from '../../Components/Common/Globalpagination';

const getPlainText = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return (tempDiv.textContent || tempDiv.innerText || '').trim();
};

const safeParseArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const SearchGurus = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [gurus, setGurus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentGuru, setCurrentGuru] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        title: '',
        yogaAlliance: '',
        location: '',
        website: '',
        email: '',
        aboutUs: '',
        isActive: true,
        heading: '',
        metaTitle: '',
        metaDescription: '',
        metaKeywords: '',
        metaOutlet: [],
        canonicalUrl: '',
        isIndex: true,
        imageAltText: ''
    });
    const [newMetaOutletInput, setNewMetaOutletInput] = useState('');
    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [ogImage, setOgImage] = useState(null);
    const [ogImagePreview, setOgImagePreview] = useState(null);
    const [ogImageError, setOgImageError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [modalError, setModalError] = useState(null);

    const handleAddMetaOutletItem = () => {
        const trimmed = newMetaOutletInput.trim();
        if (!trimmed) return;
        setFormData(prev => ({
            ...prev,
            metaOutlet: [...(prev.metaOutlet || []), trimmed]
        }));
        setNewMetaOutletInput('');
        if (fieldErrors.metaOutlet) {
            setFieldErrors(prev => {
                const updated = { ...prev };
                delete updated.metaOutlet;
                return updated;
            });
        }
    };

    const handleRemoveMetaOutletItem = (index) => {
        setFormData(prev => ({
            ...prev,
            metaOutlet: (prev.metaOutlet || []).filter((_, idx) => idx !== index)
        }));
    };

    // OG Image Handlers
    const handleOgImageChange = (e) => {
        const file = e.target.files && e.target.files[0];
        const inputElement = e.target;
        if (file) {
            if (!file.type.startsWith('image/')) {
                setOgImageError('Only image files are allowed.');
                setOgImage(null);
                setOgImagePreview(null);
                inputElement.value = "";
                return;
            }
            if (file.size > 2 * 1024 * 1024) {
                setOgImageError('Image size must be less than 2MB.');
                setOgImage(null);
                setOgImagePreview(null);
                inputElement.value = "";
                return;
            }
            setOgImage(file);
            setOgImageError('');
            const reader = new FileReader();
            reader.onloadend = () => {
                setOgImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveOgImage = () => {
        setOgImage(null);
        setOgImagePreview(null);
        setOgImageError('');
    };

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedGuruId, setSelectedGuruId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    // Pagination states
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setPage(1);
    }, [debouncedSearchTerm, statusFilter]);

    // Fetch gurus
    const fetchGurusData = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: page
            };
            if (rowsPerPage !== 10) params.limit = rowsPerPage;
            const response = await getAllSearchGurus(debouncedSearchTerm, statusFilter, params);
            if (response && response.success) {
                setGurus(response.data);
                setTotalPages(response.totalPages || 1);
                setTotalItems(response.totalItems || 0);
            } else {
                setError("Failed to load gurus.");
            }
        } catch (err) {
            setError("Failed to fetch gurus. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, [debouncedSearchTerm, statusFilter, page, rowsPerPage]);

    useEffect(() => {
        fetchGurusData();
    }, [fetchGurusData]);

    const handleOpenModal = (guru = null) => {
        setFieldErrors({});
        setModalError(null);
        if (guru) {
            setIsEdit(true);
            setCurrentGuru(guru);
            setFormData({
                name: guru.name || '',
                slug: guru.slug ? cleanSlugFinal(guru.slug) : (guru.name ? generateSlug(guru.name) : ''),
                title: guru.title || '',
                yogaAlliance: guru.yogaAlliance || '',
                location: guru.location || '',
                website: guru.website || '',
                email: guru.email || '',
                aboutUs: guru.aboutUs || '',
                isActive: guru.isActive || false,
                heading: guru.heading || '',
                metaTitle: guru.metaTitle || '',
                metaDescription: guru.metaDescription || '',
                metaKeywords: guru.metaKeywords || '',
                metaOutlet: safeParseArray(guru.metaOutlet),
                canonicalUrl: guru.canonicalUrl || '',
                isIndex: guru.isIndex !== undefined ? (guru.isIndex === "true" || guru.isIndex === true) : true,
                imageAltText: guru.imageAltText || ''
            });
            setNewMetaOutletInput('');
            setIsSlugManuallyEdited(false);
            setImagePreview(guru.profileImage || null);
            setOgImagePreview(guru.ogImage || null);
            setOgImage(null);
            setOgImageError('');
        } else {
            setIsEdit(false);
            setCurrentGuru(null);
            setFormData({
                name: '',
                slug: '',
                title: '',
                yogaAlliance: '',
                location: '',
                website: '',
                email: '',
                aboutUs: '',
                isActive: true,
                heading: '',
                metaTitle: '',
                metaDescription: '',
                metaKeywords: '',
                metaOutlet: [],
                canonicalUrl: '',
                isIndex: true,
                imageAltText: ''
            });
            setNewMetaOutletInput('');
            setIsSlugManuallyEdited(false);
            setImagePreview(null);
            setOgImage(null);
            setOgImagePreview(null);
            setOgImageError('');
        }
        setSelectedImage(null);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setFieldErrors({});
        setModalError(null);
        setOpenModal(false);
        setIsEdit(false);
        setCurrentGuru(null);
        setFormData({
            name: '',
            slug: '',
            title: '',
            yogaAlliance: '',
            location: '',
            website: '',
            email: '',
            aboutUs: '',
            isActive: true,
            heading: '',
            metaTitle: '',
            metaDescription: '',
            metaKeywords: '',
            metaOutlet: [],
            canonicalUrl: '',
            isIndex: true,
            imageAltText: ''
        });
        setNewMetaOutletInput('');
        setIsSlugManuallyEdited(false);
        setSelectedImage(null);
        setImagePreview(null);
        setOgImage(null);
        setOgImagePreview(null);
        setOgImageError('');
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'name') {
            const sanitizedValue = value.replace(/[^a-zA-Z\s]/g, '');
            setFormData(prev => ({
                ...prev,
                name: sanitizedValue,
                ...(!isSlugManuallyEdited ? { slug: generateSlug(sanitizedValue) } : {})
            }));
            if (fieldErrors.name) {
                setFieldErrors(prev => ({
                    ...prev,
                    name: null
                }));
            }
            if (!isSlugManuallyEdited && fieldErrors.slug) {
                setFieldErrors(prev => ({
                    ...prev,
                    slug: null
                }));
            }
            return;
        }

        const newValue = type === 'checkbox' ? checked : value;
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        if (name === 'location') {
            if (value && value.trim() && !/^[a-zA-Z0-9]/.test(value.trim())) {
                setFieldErrors(prev => ({
                    ...prev,
                    location: 'Location cannot start with special characters'
                }));
                return;
            }
        }

        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const handleSlugChange = (e) => {
        const rawVal = e.target.value;
        const normalizedVal = normalizeSlugInput(rawVal);
        setFormData(prev => ({
            ...prev,
            slug: normalizedVal
        }));
        setIsSlugManuallyEdited(true);
        if (fieldErrors.slug) {
            setFieldErrors(prev => ({
                ...prev,
                slug: null
            }));
        }
    };

    const handleSlugBlur = () => {
        if (!formData.slug) {
            setFormData(prev => ({ ...prev, slug: '' }));
            return;
        }
        const cleaned = cleanSlugFinal(formData.slug);
        if (cleaned !== formData.slug) {
            setFormData(prev => ({ ...prev, slug: cleaned }));
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            if (!file.type.startsWith('image/')) {
                setFieldErrors(prev => ({
                    ...prev,
                    teacherProfileImage: 'Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed'
                }));
                showSnackbar("Only image files are allowed", "error");
                e.target.value = '';
                return;
            }

            // if (file.size > 2 * 1024 * 1024) {
            //     setFieldErrors(prev => ({
            //         ...prev,
            //         teacherProfileImage: 'Image size must be less than 2MB'
            //     }));
            //     showSnackbar("Image size must be less than 2MB", "error");
            //     e.target.value = '';
            //     return;
            // }

            setSelectedImage(file);

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);

            if (fieldErrors.teacherProfileImage) {
                setFieldErrors(prev => {
                    const updated = { ...prev };
                    delete updated.teacherProfileImage;
                    return updated;
                });
            }
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fieldErrors.teacherProfileImage) {
            setFieldErrors(prev => {
                const updated = { ...prev };
                delete updated.teacherProfileImage;
                return updated;
            });
        }
    };

    const handleSubmit = async () => {
        const newFieldErrors = {};
        if (!formData.name || !formData.name.trim()) {
            newFieldErrors.name = "Name is required";
        } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
            newFieldErrors.name = "Name cannot contain numbers or special characters";
        }

        const finalSlug = cleanSlugFinal(formData.slug);
        if (!finalSlug) {
            newFieldErrors.slug = "Slug is required";
        } else if (gurus.some(g => g.id !== currentGuru?.id && cleanSlugFinal(g.slug) === finalSlug)) {
            newFieldErrors.slug = "This slug is already in use by another facilitator";
        }

        if (!formData.title || !formData.title.trim()) {
            newFieldErrors.title = "Title is required";
        }

        if (formData.location && formData.location.trim() && !/^[a-zA-Z0-9]/.test(formData.location.trim())) {
            newFieldErrors.location = "Location cannot start with special characters";
        }

        const plainText = getPlainText(formData.aboutUs);
        const charCount = plainText.length;
        if (charCount < 700 || charCount > 1000) {
            newFieldErrors.aboutUs = `About Us must be between 700 and 1000 characters. Current: ${charCount} characters`;
        }

        if (selectedImage && selectedImage instanceof File) {
            if (!selectedImage.type.startsWith('image/')) {
                setFieldErrors(prev => ({
                    ...prev,
                    teacherProfileImage: 'Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed'
                }));
                showSnackbar("Only image files are allowed", "error");
                return;
            }
            if (selectedImage.size > 2 * 1024 * 1024) {
                setFieldErrors(prev => ({
                    ...prev,
                    teacherProfileImage: 'Image size must be less than 2MB'
                }));
                showSnackbar("Image size must be less than 2MB", "error");
                return;
            }
        }

        if (Object.keys(newFieldErrors).length > 0) {
            setFieldErrors(newFieldErrors);
            setModalError("Please fill in all required fields and correct validation errors");
            return;
        }

        setSubmitting(true);
        setModalError(null);
        setFieldErrors({});

        const data = new FormData();
        data.append('name', formData.name.trim());
        data.append('slug', finalSlug);
        data.append('title', formData.title);
        data.append('yogaAlliance', formData.yogaAlliance);
        data.append('location', formData.location);
        data.append('website', formData.website);
        data.append('email', formData.email);
        data.append('aboutUs', formData.aboutUs);
        data.append('isActive', formData.isActive);

        data.append('heading', formData.heading ? formData.heading.trim() : '');
        data.append('metaTitle', formData.metaTitle ? formData.metaTitle.trim() : '');
        data.append('metaDescription', formData.metaDescription ? formData.metaDescription.trim() : '');
        data.append('metaKeywords', formData.metaKeywords ? formData.metaKeywords.trim() : '');
        data.append('metaOutlet', JSON.stringify(formData.metaOutlet || []));
        data.append('canonicalUrl', formData.canonicalUrl ? formData.canonicalUrl.trim() : '');
        data.append('isIndex', formData.isIndex ? "true" : "false");
        data.append('imageAltText', formData.imageAltText ? formData.imageAltText.trim() : '');

        // Add OG Image if present
        if (ogImage) {
            data.append('ogImage', ogImage);
        }

        if (selectedImage) {
            data.append('teacherProfileImage', selectedImage);
        }

        try {
            if (isEdit) {
                await updateSearchGuru(currentGuru.id, data);
                showSnackbar("Guru updated successfully!", "success");
            } else {
                await createSearchGuru(data);
                showSnackbar("Guru created successfully!", "success");
            }

            handleCloseModal();
            fetchGurusData();
        } catch (err) {
            console.error("Error saving guru:", err);
            const responseData = err.response?.data;
            const errMsg = responseData?.message || err.message || `Failed to ${isEdit ? 'update' : 'create'} guru`;

            const mapMessageToField = (messageStr, targetObj) => {
                const msg = messageStr.toLowerCase();
                if (msg.includes('name')) {
                    targetObj['name'] = messageStr;
                } else if (msg.includes('title') && !msg.includes('metatitle') && !msg.includes('meta title')) {
                    targetObj['title'] = messageStr;
                } else if (msg.includes('yoga alliance') || msg.includes('yogaalliance')) {
                    targetObj['yogaAlliance'] = messageStr;
                } else if (msg.includes('location')) {
                    targetObj['location'] = messageStr;
                } else if (msg.includes('website')) {
                    targetObj['website'] = messageStr;
                } else if (msg.includes('email')) {
                    targetObj['email'] = messageStr;
                } else if (msg.includes('about') || msg.includes('aboutus')) {
                    targetObj['aboutUs'] = messageStr;
                } else if (msg.includes('image') || msg.includes('profile')) {
                    targetObj['teacherProfileImage'] = messageStr;
                } else if (msg.includes('heading')) {
                    targetObj['heading'] = messageStr;
                } else if (msg.includes('metatitle') || msg.includes('meta title')) {
                    targetObj['metaTitle'] = messageStr;
                } else if (msg.includes('metadescription') || msg.includes('meta description')) {
                    targetObj['metaDescription'] = messageStr;
                } else if (msg.includes('metakeywords') || msg.includes('meta keywords')) {
                    targetObj['metaKeywords'] = messageStr;
                } else if (msg.includes('metaoutlet') || msg.includes('meta outlet')) {
                    targetObj['metaOutlet'] = messageStr;
                } else if (msg.includes('canonicalurl') || msg.includes('canonical url')) {
                    targetObj['canonicalUrl'] = messageStr;
                } else if (msg.includes('isindex') || msg.includes('is index')) {
                    targetObj['isIndex'] = messageStr;
                }
            };

            if (responseData?.details && Array.isArray(responseData.details)) {
                const errorsObj = {};
                responseData.details.forEach(item => {
                    if (item.field && item.message) {
                        const fieldName = item.field.toLowerCase();
                        if (fieldName === 'general') {
                            mapMessageToField(item.message, errorsObj);
                        } else if (fieldName === 'teacherprofileimage' || fieldName === 'profileimage') {
                            errorsObj['teacherProfileImage'] = item.message;
                        } else {
                            errorsObj[item.field] = item.message;
                        }
                    }
                });
                setFieldErrors(errorsObj);
                if (Object.keys(errorsObj).length === 0) {
                    setModalError(responseData.message || "Validation failed");
                }
            } else if (responseData?.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                const errorsObj = {};
                responseData.errors.forEach(errStr => {
                    mapMessageToField(errStr, errorsObj);
                });
                setFieldErrors(errorsObj);
                if (Object.keys(errorsObj).length === 0) {
                    setModalError(responseData.errors.join(', '));
                }
            } else {
                const errorsObj = {};
                mapMessageToField(errMsg, errorsObj);
                if (Object.keys(errorsObj).length > 0) {
                    setFieldErrors(errorsObj);
                } else {
                    setModalError(errMsg);
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedGuruId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedGuruId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteSearchGuru(selectedGuruId);
                if (response && response.success) {
                    showSnackbar("Guru deleted successfully!", "success");
                    setDeleteModalOpen(false);
                    setSelectedGuruId(null);
                    fetchGurusData();
                } else {
                    const errMsg = response?.message || "Failed to delete guru";
                    setDeleteError(errMsg);
                    showSnackbar(errMsg, "error");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const responseData = err.response?.data;
                const errorMessage = responseData?.errors?.[0] ||
                    responseData?.details?.[0]?.message ||
                    responseData?.message ||
                    err.message ||
                    "Failed to delete guru";
                setDeleteError(errorMessage);
                showSnackbar(errorMessage, "error");
            } finally {
                setIsDeleting(false);
            }
        }
    };

    const aboutUsCharCount = getPlainText(formData.aboutUs).length;

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                {/* Header Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.5rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Facilitators
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button onClick={() => handleOpenModal()}
                            disableElevation disableRipple disableFocusRipple disableTouchRipple
                            variant="contained" startIcon={<Plus size={18} />}
                            sx={{
                                bgcolor: '#FFBD65',
                                px: 2.5,
                                color: '#fff',
                                borderRadius: '6px',
                                height: '40px',
                                textTransform: 'none',
                                fontSize: '0.85rem'
                            }}
                        >
                            Add Guru
                        </Button>
                    </PermissionWrapper>
                </Box>

                {/* Search and Filter Section */}
                <Box sx={{ mb: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <TextField
                        size="small" placeholder="Search by name, title, location..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{
                            bgcolor: '#fff', width: { xs: "100%", md: "30%" },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '6px',
                                height: '40px',
                                fontSize: '0.85rem',
                                '& fieldset': { borderColor: '#E0E0E0' },
                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                    <Search size={16} color="#999" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Select
                        size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        displayEmpty
                        sx={{
                            minWidth: 130,
                            bgcolor: '#fff',
                            borderRadius: '6px',
                            height: '40px',
                            fontSize: '0.85rem',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFBD65', borderWidth: '1px' }
                        }}
                    >
                        <MenuItem value="" sx={{ fontSize: '0.85rem' }}>All Status</MenuItem>
                        <MenuItem value="true" sx={{ fontSize: '0.85rem' }}>Active</MenuItem>
                        <MenuItem value="false" sx={{ fontSize: '0.85rem' }}>Inactive</MenuItem>
                    </Select>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1250 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '60px' }}>Profile</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Slug</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Title</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Location</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Yoga Alliance</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={13} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : gurus.length > 0 ? (
                                    gurus.map((guru, idx) => (
                                        <TableRow key={guru.id} sx={{
                                             transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Avatar src={guru.profileImage} variant="rounded" sx={{ width: 32, height: 32, fontSize: '0.9rem', borderRadius: '4px' }}>
                                                    {guru.name?.charAt(0)}
                                                </Avatar>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 550, color: '#333' }}>{guru.name}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={guru.slug}>
                                                {guru.slug || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{guru.title}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{guru.location}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555' }}>{guru.yogaAlliance}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    px: 1.5, py: 0.3,
                                                    borderRadius: '4px',
                                                    bgcolor: guru.isActive ? '#E8F5E9' : '#FFEBEE',
                                                    color: guru.isActive ? '#2E7D32' : '#D32F2F',
                                                    fontSize: '0.7rem', fontWeight: 600
                                                }}>
                                                    {guru.isActive ? 'Active' : 'Inactive'}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {guru.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {guru.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(guru.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(guru.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenModal(guru)}>
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                                size="small" sx={{ color: '#FF4D49' }} onClick={() => handleDeleteClick(guru.id)}>
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={13} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No gurus found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <GlobalPagination
                        page={page}
                        pageSize={rowsPerPage}
                        totalRecords={totalItems}
                        totalPages={totalPages}
                        onPageChange={(newPage) => setPage(newPage)}
                        onPageSizeChange={(newSize) => {
                            setRowsPerPage(newSize);
                            setPage(1);
                        }}
                        loading={loading}
                    />
                </Paper>

                {/* Add/Edit Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Guru' : 'Add New Guru'}
                    </DialogTitle>
                    <DialogContent>
                        {modalError && (
                            <Alert severity="error" sx={{ mt: 1, mb: 1, py: 0.5, fontSize: '0.85rem' }} onClose={() => setModalError(null)}>
                                {modalError}
                            </Alert>
                        )}
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField size='small'
                                    fullWidth
                                    label="Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    error={Boolean(fieldErrors.name)}
                                    helperText={fieldErrors.name}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                                <TextField size='small'
                                    fullWidth
                                    label="Title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                    error={Boolean(fieldErrors.title)}
                                    helperText={fieldErrors.title}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                            </Box>

                            <TextField size='small'
                                fullWidth
                                label="Slug"
                                name="slug"
                                value={formData.slug}
                                onChange={handleSlugChange}
                                onBlur={handleSlugBlur}
                                placeholder="e.g. john-wellness-academy"
                                required
                                error={Boolean(fieldErrors.slug)}
                                helperText={fieldErrors.slug || "Automatically generated from name."}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        fontFamily: 'monospace',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField size='small'
                                    fullWidth
                                    label="Yoga Alliance"
                                    name="yogaAlliance"
                                    value={formData.yogaAlliance}
                                    onChange={handleInputChange}
                                    error={Boolean(fieldErrors.yogaAlliance)}
                                    helperText={fieldErrors.yogaAlliance}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                                <TextField size='small'
                                    fullWidth
                                    label="Location"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    error={Boolean(fieldErrors.location)}
                                    helperText={fieldErrors.location}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField size='small'
                                    fullWidth
                                    label="Website / Social link"
                                    name="website"
                                    required
                                    value={formData.website}
                                    onChange={handleInputChange}
                                    error={Boolean(fieldErrors.website)}
                                    helperText={fieldErrors.website}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                                <TextField size='small'
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    error={Boolean(fieldErrors.email)}
                                    helperText={fieldErrors.email}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: '#333', fontSize: '0.85rem' }}>About Us *</Typography>
                                <Box sx={{
                                    border: fieldErrors.aboutUs ? '1px solid #FF4D49' : 'none',
                                    borderRadius: '6px',
                                    '& .ql-toolbar': { borderTopLeftRadius: '6px', borderTopRightRadius: '6px', borderColor: '#E0E0E0' },
                                    '& .ql-container': { borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px', borderColor: '#E0E0E0', fontSize: '0.85rem', minHeight: '150px' }
                                }}>
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.aboutUs}
                                        onChange={(value) => {
                                            setFormData(prev => ({ ...prev, aboutUs: value }));
                                            if (fieldErrors.aboutUs) {
                                                setFieldErrors(prev => ({ ...prev, aboutUs: null }));
                                            }
                                        }}
                                        style={{
                                            height: '200px',
                                            marginBottom: '50px'
                                        }}
                                        modules={{
                                            toolbar: [
                                                [{ 'header': [1, 2, 3, false] }],
                                                ['bold', 'italic', 'underline', 'strike'],
                                                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                                ['link'],
                                                ['clean']
                                            ]
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 1 }}>
                                    <Box sx={{ flex: 1, mr: 2 }}>
                                        {fieldErrors.aboutUs && (
                                            <Typography variant="caption" color="error" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                {fieldErrors.aboutUs}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: (aboutUsCharCount >= 700 && aboutUsCharCount <= 1000)
                                                ? '#2E7D32'
                                                : (aboutUsCharCount > 1000 ? '#D32F2F' : '#666'),
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.75rem'
                                        }}
                                    >
                                        {aboutUsCharCount} / 1000 characters (700 min)
                                    </Typography>
                                </Box>
                            </Box>

                            <FormControlLabel
                                control={<Switch checked={formData.isActive} onChange={handleInputChange} name="isActive"
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                    }}
                                />}
                                label={<Typography sx={{ fontSize: '0.85rem' }}>Active</Typography>}
                            />

                            <Box>
                                <Typography sx={{ mb: 1, fontWeight: 550, color: fieldErrors.teacherProfileImage ? '#d32f2f' : '#333', fontSize: '0.85rem' }}>Profile Image</Typography>
                                <Box
                                    sx={{
                                        border: fieldErrors.teacherProfileImage ? '2px dashed #d32f2f' : '2px dashed #E0E0E0',
                                        borderRadius: '6px', p: 2,
                                        textAlign: 'center',
                                        bgcolor: '#FAFAFA',
                                        cursor: 'pointer',
                                        position: 'relative',
                                    }}
                                >
                                    {imagePreview ? (
                                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                            <img src={imagePreview} alt="Preview"
                                                style={{ maxHeight: 100, maxWidth: '100%', borderRadius: '4px' }}
                                            />
                                            <IconButton disableFocusRipple disableRipple disableTouchRipple
                                                size="small"
                                                onClick={handleRemoveImage}
                                                sx={{
                                                    position: 'absolute',
                                                    top: -10,
                                                    right: -10,
                                                    bgcolor: '#FF4D49',
                                                    color: '#fff',
                                                    padding: '2px',
                                                }}
                                            >
                                                <X size={12} />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <label style={{ width: '100%', height: '100%', display: 'block', cursor: 'pointer' }}>
                                            <input type="file" accept="image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml" hidden onChange={handleImageChange} />
                                            <Stack spacing={1} alignItems="center">
                                                <Box sx={{ p: 1, borderRadius: '50%', bgcolor: '#FFF8ED', color: '#FFBD65', display: 'inline-flex' }}>
                                                    <Upload size={18} />
                                                </Box>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>Click to upload profile image</Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#999' }}>PNG, JPG (max. 2MB)</Typography>
                                            </Stack>
                                        </label>
                                    )}
                                </Box>
                                {fieldErrors.teacherProfileImage && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', pl: 1, fontSize: '0.75rem' }}>
                                        {fieldErrors.teacherProfileImage}
                                    </Typography>
                                )}
                            </Box>

                            {/* SEO Module */}
                            <Box sx={{
                                border: '1px solid #E2E8F0',
                                borderRadius: '6px',
                                p: 2,
                                bgcolor: '#FAFAFA'
                            }}>
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
                                    SEO
                                </Typography>
                                <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 1.5 }}>
                                    Configure search engine optimization settings for this facilitator.
                                </Typography>
                                <Stack spacing={1.5}>
                                    {/* Heading */}
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Heading"
                                        name="heading"
                                        value={formData.heading}
                                        onChange={handleInputChange}
                                        placeholder="Enter heading..."
                                        error={Boolean(fieldErrors.heading)}
                                        helperText={fieldErrors.heading}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                bgcolor: '#FFFFFF',
                                                '& fieldset': { borderColor: '#E0E0E0' },
                                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            }
                                        }}
                                    />

                                    {/* Meta Title */}
                                    <Box>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            label="Meta Title"
                                            name="metaTitle"
                                            value={formData.metaTitle}
                                            onChange={handleInputChange}
                                            placeholder="Enter meta title..."
                                            inputProps={{ maxLength: 70 }}
                                            error={Boolean(fieldErrors.metaTitle)}
                                            helperText={fieldErrors.metaTitle}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem',
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E0E0E0' },
                                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                }
                                            }}
                                        />
                                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', textAlign: 'right', mt: 0.5, fontSize: '0.72rem' }}>
                                            {formData.metaTitle?.length || 0} / 60 characters
                                        </Typography>
                                    </Box>

                                    {/* Meta Description */}
                                    <Box>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Meta Description"
                                            name="metaDescription"
                                            value={formData.metaDescription}
                                            onChange={handleInputChange}
                                            placeholder="Enter meta description..."
                                            inputProps={{ maxLength: 180 }}
                                            error={Boolean(fieldErrors.metaDescription)}
                                            helperText={fieldErrors.metaDescription}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem',
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E0E0E0' },
                                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                }
                                            }}
                                        />
                                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', textAlign: 'right', mt: 0.5, fontSize: '0.72rem' }}>
                                            {formData.metaDescription?.length || 0} / 160 characters
                                        </Typography>
                                    </Box>

                                    {/* Meta Keywords */}
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Meta Keywords"
                                        name="metaKeywords"
                                        value={formData.metaKeywords}
                                        onChange={handleInputChange}
                                        placeholder="Enter meta keywords (comma separated)..."
                                        error={Boolean(fieldErrors.metaKeywords)}
                                        helperText={fieldErrors.metaKeywords || "Comma separated keywords"}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                bgcolor: '#FFFFFF',
                                                '& fieldset': { borderColor: '#E0E0E0' },
                                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            }
                                        }}
                                    />

                                    {/* Meta Outlet */}
                                    <Box>
                                        <Typography sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.825rem', color: '#334155' }}>
                                            Meta Outlet (H2 Outlet)
                                        </Typography>
                                        <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 1.5 }}>
                                            Add meta outlet bullet points for this facilitator.
                                        </Typography>

                                        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                                            <TextField
                                                size="small"
                                                placeholder="Add meta outlet item..."
                                                value={newMetaOutletInput}
                                                onChange={(e) => setNewMetaOutletInput(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleAddMetaOutletItem();
                                                    }
                                                }}
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '6px',
                                                        fontSize: '0.85rem',
                                                        bgcolor: '#FFFFFF',
                                                        '& fieldset': { borderColor: '#E0E0E0' },
                                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                    }
                                                }}
                                            />
                                            <Button
                                                variant="contained"
                                                startIcon={<Plus size={16} />}
                                                onClick={handleAddMetaOutletItem}
                                                sx={{
                                                    backgroundColor: '#1E293B',
                                                    color: '#FFFFFF',
                                                    textTransform: 'none',
                                                    boxShadow: 'none',
                                                    borderRadius: '6px',
                                                    fontWeight: 500,
                                                    px: 2.5,
                                                    fontSize: '0.8rem',
                                                    whiteSpace: 'nowrap',
                                                    '&:hover': { backgroundColor: '#334155', boxShadow: 'none' }
                                                }}
                                            >
                                                Add
                                            </Button>
                                        </Stack>

                                        {formData.metaOutlet && formData.metaOutlet.length > 0 && (
                                            <Stack spacing={1}>
                                                {formData.metaOutlet.map((item, idx) => (
                                                    <Box
                                                        key={idx}
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            p: 1.25,
                                                            backgroundColor: '#FFFFFF',
                                                            border: '1px solid #E2E8F0',
                                                            borderRadius: '6px'
                                                        }}
                                                    >
                                                        <Typography sx={{ color: '#334155', fontSize: '0.8rem', fontWeight: 500 }}>
                                                            • {item}
                                                        </Typography>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRemoveMetaOutletItem(idx)}
                                                            sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}
                                                        >
                                                            <Trash2 size={15} />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        )}
                                        {fieldErrors.metaOutlet && (
                                            <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}>
                                                {fieldErrors.metaOutlet}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Canonical URL */}
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Canonical URL"
                                        name="canonicalUrl"
                                        value={formData.canonicalUrl}
                                        onChange={handleInputChange}
                                        placeholder="Enter canonical URL..."
                                        error={Boolean(fieldErrors.canonicalUrl)}
                                        helperText={fieldErrors.canonicalUrl}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.85rem',
                                                bgcolor: '#FFFFFF',
                                                '& fieldset': { borderColor: '#E0E0E0' },
                                                '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            }
                                        }}
                                    />

                                    {/* Is Index */}
                                    <Box>
                                        <Typography sx={{ mb: 0.75, fontSize: '0.825rem', fontWeight: 500, color: "#334155" }}>
                                            Is Index
                                        </Typography>
                                        <Box
                                            sx={{
                                                border: '1px solid #E2E8F0',
                                                borderRadius: '6px',
                                                boxShadow: 'none',
                                                p: 1.25,
                                                bgcolor: '#FFFFFF'
                                            }}
                                        >
                                            <Stack direction="row" spacing={3} alignItems="center">
                                                <Box
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, isIndex: true }));
                                                        if (fieldErrors.isIndex) {
                                                            setFieldErrors(prev => {
                                                                const updated = { ...prev };
                                                                delete updated.isIndex;
                                                                return updated;
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="guruIsIndex"
                                                        checked={formData.isIndex === true}
                                                        onChange={() => {
                                                            setFormData(prev => ({ ...prev, isIndex: true }));
                                                            if (fieldErrors.isIndex) {
                                                                setFieldErrors(prev => {
                                                                    const updated = { ...prev };
                                                                    delete updated.isIndex;
                                                                    return updated;
                                                                });
                                                            }
                                                        }}
                                                        style={{ cursor: 'pointer', margin: 0 }}
                                                    />
                                                    <Typography sx={{ fontSize: '0.825rem', color: '#334155', cursor: 'pointer' }}>True (Indexable)</Typography>
                                                </Box>
                                                <Box
                                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, isIndex: false }));
                                                        if (fieldErrors.isIndex) {
                                                            setFieldErrors(prev => {
                                                                const updated = { ...prev };
                                                                delete updated.isIndex;
                                                                return updated;
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="guruIsIndex"
                                                        checked={formData.isIndex === false}
                                                        onChange={() => {
                                                            setFormData(prev => ({ ...prev, isIndex: false }));
                                                            if (fieldErrors.isIndex) {
                                                                setFieldErrors(prev => {
                                                                    const updated = { ...prev };
                                                                    delete updated.isIndex;
                                                                    return updated;
                                                                });
                                                            }
                                                        }}
                                                        style={{ cursor: 'pointer', margin: 0 }}
                                                    />
                                                    <Typography sx={{ fontSize: '0.825rem', color: '#334155', cursor: 'pointer' }}>False (No Index)</Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                        {fieldErrors.isIndex && (
                                            <Typography variant="caption" sx={{ color: "#d32f2f", mt: 0.5, display: "block" }}>
                                                {fieldErrors.isIndex}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Image Alt Text */}
                                    <Box>
                                        <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>
                                            Image Alt Text
                                        </Typography>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            placeholder="Enter image alt text"
                                            value={formData.imageAltText}
                                            onChange={(e) => setFormData(prev => ({ ...prev, imageAltText: e.target.value }))}
                                            error={Boolean(fieldErrors.imageAltText)}
                                            helperText={fieldErrors.imageAltText}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem',
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E0E0E0' },
                                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                                }
                                            }}
                                        />
                                    </Box>

                                    {/* OG Image */}
                                    <Box>
                                        <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>
                                            OG Image
                                        </Typography>
                                        {!ogImagePreview ? (
                                            <Box
                                                component="label"
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    border: ogImageError ? "1px dashed #EF4444" : "1px dashed #CBD5E1",
                                                    borderRadius: "6px",
                                                    height: 140,
                                                    cursor: submitting ? "not-allowed" : "pointer",
                                                    bgcolor: "#F8FAFC",
                                                    transition: "all 0.2s ease-in-out",
                                                    "&:hover": {
                                                        borderColor: ogImageError ? "#EF4444" : "#6E8964",
                                                        bgcolor: "#F1F5F9"
                                                    }
                                                }}
                                            >
                                                <input
                                                    type="file"
                                                    accept="image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml, image/*"
                                                    style={{ display: "none" }}
                                                    disabled={submitting}
                                                    onChange={handleOgImageChange}
                                                />
                                                <Upload size={22} color={ogImageError ? "#EF4444" : "#64748B"} style={{ marginBottom: 6 }} />
                                                <Typography sx={{ color: ogImageError ? "#EF4444" : "#475569", fontSize: '0.8rem', fontWeight: 500 }}>
                                                    Click to upload OG image
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: ogImageError ? "#EF4444" : "#94A3B8", fontSize: '0.7rem' }}>
                                                    PNG, JPG or WebP (Max 2MB)
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box sx={{ position: "relative", width: "100%", height: 140, bgcolor: "#F8FAFC", borderRadius: "6px", border: "1px solid #E2E8F0", display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                <Box
                                                    component="img"
                                                    src={ogImagePreview}
                                                    alt="OG Image Preview"
                                                    sx={{
                                                        maxWidth: "100%",
                                                        maxHeight: "100%",
                                                        objectFit: "contain",
                                                    }}
                                                />
                                                <IconButton
                                                    onClick={handleRemoveOgImage}
                                                    disabled={submitting}
                                                    sx={{
                                                        position: "absolute",
                                                        top: 6,
                                                        right: 6,
                                                        backgroundColor: "#FFFFFF",
                                                        border: "1px solid #E2E8F0",
                                                        color: "#EF4444",
                                                        p: 0.5,
                                                        boxShadow: 'none',
                                                        '&:hover': { bgcolor: '#FEE2E2' }
                                                    }}
                                                    size="small"
                                                >
                                                    <X size={14} />
                                                </IconButton>
                                            </Box>
                                        )}
                                        {ogImageError && (
                                            <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.75, fontWeight: 400 }}>
                                                {ogImageError}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple onClick={handleCloseModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>
                            Cancel
                        </Button>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit} disabled={submitting} variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                color: '#fff',
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Create')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Modal */}
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Guru"
                    description="Are you sure you want to delete this guru? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default SearchGurus;
